import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

interface LandingCTAProps {
  title: string;
  subtitle?: string;
  ctaPrimary: string;
  ctaSecondary?: string;
  ctaPrimaryHref?: string;
  ctaSecondaryHref?: string;
  variant?: 'default' | 'gradient' | 'dark';
  /** Small guarantee text below the CTA buttons */
  guarantee?: string;
}

/**
 * LandingCTA - Call-to-action section
 *
 * Features:
 * - Multiple visual variants
 * - Dual CTA buttons
 * - Eye-catching design
 * - Mobile-optimized
 * - Decorative orb for gradient/dark variants
 *
 * @example
 * <LandingCTA
 *   title="Ready to Get Started?"
 *   subtitle="Join thousands of professionals using Sloty"
 *   ctaPrimary="Start Free Trial"
 *   ctaSecondary="Schedule Demo"
 *   variant="gradient"
 * />
 */
export const LandingCTA = ({
  title,
  subtitle,
  ctaPrimary,
  ctaSecondary,
  ctaPrimaryHref = '/register',
  ctaSecondaryHref = '#',
  variant = 'default',
  guarantee,
}: LandingCTAProps) => {
  const variants = {
    default: 'bg-slate-50',
    gradient: 'bg-gradient-to-r from-brand-600 via-brand-700 to-brand-800',
    dark: 'bg-slate-900',
  };

  const textColor = variant === 'default' ? 'text-slate-900' : 'text-white';
  const subtitleColor = variant === 'default' ? 'text-slate-600' : 'text-brand-100';

  return (
    <section className={`relative py-16 sm:py-20 ${variants[variant]} overflow-hidden`}>
      {/* Decorative orb for gradient/dark variants */}
      {variant !== 'default' && (
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      )}

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Title */}
        <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${textColor} mb-4`}>
          {title}
        </h2>

        {/* Subtitle */}
        {subtitle && (
          <p className={`text-lg sm:text-xl ${subtitleColor} mb-8 max-w-2xl mx-auto`}>
            {subtitle}
          </p>
        )}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link href={ctaPrimaryHref}>
            <Button
              size="lg"
              className={`w-full sm:w-auto text-base whitespace-nowrap ${
                variant !== 'default' ? 'bg-white text-brand-600 hover:bg-slate-100' : ''
              }`}
            >
              {ctaPrimary}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          {ctaSecondary && (
            ctaSecondaryHref.includes('#') ? (
              <a href={ctaSecondaryHref}>
                <Button
                  size="lg"
                  variant={variant === 'default' ? 'outline' : 'secondary'}
                  className={`w-full sm:w-auto text-base whitespace-nowrap ${
                    variant !== 'default' ? 'bg-white/10 text-white border-white/30 hover:bg-white/20' : ''
                  }`}
                >
                  {ctaSecondary}
                </Button>
              </a>
            ) : (
              <Link href={ctaSecondaryHref}>
                <Button
                  size="lg"
                  variant={variant === 'default' ? 'outline' : 'secondary'}
                  className={`w-full sm:w-auto text-base whitespace-nowrap ${
                    variant !== 'default' ? 'bg-white/10 text-white border-white/30 hover:bg-white/20' : ''
                  }`}
                >
                  {ctaSecondary}
                </Button>
              </Link>
            )
          )}
        </div>

        {guarantee && (
          <p className={`mt-6 text-sm ${variant === 'default' ? 'text-slate-600' : 'text-white/70'}`}>
            {guarantee}
          </p>
        )}
      </div>
    </section>
  );
};
